import { supabase } from '../supabaseClient';

const CHECKOUT_TIMEOUT_MS = 20000;

export const STRIPE_PRICE_CONFIG = [
  { key: 'standard', env: null, label: 'Standard', price: 'Free', amount: 0, tier: 'standard', councilCount: 0 },
  { key: 'pro_1', env: 'REACT_APP_STRIPE_PRICE_PRO_1', label: '1 Council', price: '$99/mo', amount: 9900, tier: 'pro', councilCount: 1 },
  { key: 'pro_2', env: 'REACT_APP_STRIPE_PRICE_PRO_2', label: '2 Councils', price: '$149/mo', amount: 14900, tier: 'pro', councilCount: 2 },
  { key: 'pro_3', env: 'REACT_APP_STRIPE_PRICE_PRO_3', label: '3 Councils', price: '$199/mo', amount: 19900, tier: 'pro', councilCount: 3 },
  { key: 'pro_all', env: 'REACT_APP_STRIPE_PRICE_PRO_ALL', label: 'All 4 Councils', price: '$249/mo', amount: 24900, tier: 'pro', councilCount: 4 },
];

export const FREE_STANDARD_PLAN = STRIPE_PRICE_CONFIG.find(({ key }) => key === 'standard');
export const PAID_STRIPE_PRICE_CONFIG = STRIPE_PRICE_CONFIG.filter(({ amount }) => amount > 0);

export const PRICE_IDS = Object.fromEntries(
  STRIPE_PRICE_CONFIG.map(({ key, env }) => [key, env ? process.env[env] || '' : ''])
);

export const MISSING_STRIPE_PRICE_ENVS = STRIPE_PRICE_CONFIG
  .filter(({ key }) => key !== 'standard' && !PRICE_IDS[key])
  .map(({ env }) => env);

// Billing is fail-closed. Production paid-plan UI is only built in after an
// operator has installed live Stripe credentials and explicitly enabled it.
// Stored Stripe Price IDs remain optional because the Edge Function can create
// Checkout from a plan key if Vercel/Supabase price envs drift out of sync.
export const STRIPE_CONFIGURED = process.env.REACT_APP_STRIPE_LIVE_ENABLED === 'true';

if (STRIPE_CONFIGURED && MISSING_STRIPE_PRICE_ENVS.length > 0 && process.env.NODE_ENV !== 'test') {
  console.warn(`[SiteScope] Stripe stored price IDs missing; checkout will use server-side plan fallback: ${MISSING_STRIPE_PRICE_ENVS.join(', ')}`);
}

export const PRO_PLANS = STRIPE_CONFIGURED
  ? PAID_STRIPE_PRICE_CONFIG.map(plan => ({ ...plan, priceId: PRICE_IDS[plan.key] }))
  : [];

function withTimeout(promise, ms, message) {
  let timeoutId;
  const timeout = new Promise((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error(message)), ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timeoutId));
}

export async function createCheckoutSession(planOrPriceId) {
  if (!STRIPE_CONFIGURED) throw new Error('Live billing is not enabled');
  const plan = typeof planOrPriceId === 'object'
    ? planOrPriceId
    : STRIPE_PRICE_CONFIG.find(({ key }) => PRICE_IDS[key] === planOrPriceId);
  const priceId = typeof planOrPriceId === 'string' ? planOrPriceId : planOrPriceId?.priceId;
  const planKey = plan?.key;
  if (!priceId && !planKey) throw new Error('Checkout is not configured for this plan');

  const { data, error } = await withTimeout(
    supabase.functions.invoke('create-checkout-session', {
      body: planKey ? { planKey } : { priceId },
    }),
    CHECKOUT_TIMEOUT_MS,
    'Checkout request timed out'
  );

  if (error) throw new Error(error.message || 'Checkout failed');
  if (data?.url) {
    window.location.href = data.url;
  } else {
    throw new Error(data?.error || 'No checkout URL returned');
  }
}

export async function openCustomerPortal() {
  if (!STRIPE_CONFIGURED) throw new Error('Live billing is not enabled');
  const { data, error } = await withTimeout(
    supabase.functions.invoke('create-portal-session', { body: {} }),
    CHECKOUT_TIMEOUT_MS,
    'Billing portal request timed out'
  );

  if (error) throw new Error(error.message || 'Portal failed');
  if (data?.url) {
    window.location.href = data.url;
  } else {
    throw new Error(data?.error || 'No portal URL returned');
  }
}
